import api from './api';

export type PreProjetoEtapa =
  | 'RASCUNHO'
  | 'PUBLICADO'
  | 'SELECIONADO'
  | 'ARQUIVADO';

export interface PreProjetoEmendaDTO {
  id: string;
  codigoOficial?: string;
  parlamentarId: string;
  valor: number;
  etapa: string;
}

export interface PreProjetoDTO {
  id: string;
  /** Identificador sequencial por ano, ex.: PREPROJ_001/2026 */
  codigo: string;
  instituicaoId: string;
  instituicaoNome: string;
  instituicaoBloqueada: boolean;
  usuarioCriadorId: number;
  criadoPor: string;
  titulo: string;
  objeto: string;
  justificativa?: string;
  areaAtuacao?: string;
  funcaoCodigo?: string;
  /** Reserva do limite anual em que o projeto conta (decidida pelo backend). */
  categoriaFuncao?: 'SAUDE' | 'EDUCACAO' | null;
  tipoEmendaCodigo?: string;
  classificacao?: number;
  tipoTransferencia?: string;
  esfera?: string;
  valorSolicitado: number;
  valorContemplado: number;
  valorRestante: number;
  exercicio: number;
  publicoBeneficiario?: string;
  localExecucao?: string;
  etapa: PreProjetoEtapa;
  dataCriacao: string;
  dataAtualizacao: string;
  dataPublicacao?: string;
  selecionadoPorUsuarioId?: number;
  selecionadoPor?: string;
  dataSelecao?: string;
  emendas: PreProjetoEmendaDTO[];
}

export interface PreProjetoPayload {
  instituicaoId: string;
  titulo: string;
  objeto: string;
  justificativa?: string;
  areaAtuacao?: string;
  funcaoCodigo?: string;
  tipoEmendaCodigo?: string;
  classificacao?: number;
  tipoTransferencia?: string;
  esfera?: string;
  valorSolicitado: number;
  exercicio: number;
  publicoBeneficiario?: string;
  localExecucao?: string;
}

export interface PreProjetoRetificacaoPayload {
  valorSolicitado: number;
  funcaoCodigo: string;
  motivo: string;
}

export interface PreProjetoRetificacaoDTO {
  id: string;
  valorAnterior: number;
  valorNovo: number;
  funcaoCodigoAnterior?: string;
  funcaoCodigoNovo?: string;
  areaAtuacaoAnterior?: string;
  areaAtuacaoNova?: string;
  motivo: string;
  usuario: string;
  dataRetificacao: string;
}

const preProjetoService = {
  list: async (): Promise<PreProjetoDTO[]> => (await api.get('/pre-projetos')).data,
  catalogo: async (incluirBloqueadas = false): Promise<PreProjetoDTO[]> =>
    (await api.get('/pre-projetos/catalogo', { params: { incluirBloqueadas } })).data,
  get: async (id: string): Promise<PreProjetoDTO> => (await api.get(`/pre-projetos/${id}`)).data,
  create: async (payload: PreProjetoPayload): Promise<PreProjetoDTO> =>
    (await api.post('/pre-projetos', payload)).data,
  update: async (id: string, payload: PreProjetoPayload): Promise<PreProjetoDTO> =>
    (await api.put(`/pre-projetos/${id}`, payload)).data,
  publish: async (id: string): Promise<PreProjetoDTO> =>
    (await api.post(`/pre-projetos/${id}/publicar`)).data,
  /** Retificação: altera valor e/ou área de atuação, com motivo, dentro do período configurado. */
  retificar: async (id: string, payload: PreProjetoRetificacaoPayload): Promise<PreProjetoDTO> =>
    (await api.put(`/pre-projetos/${id}/retificacao`, payload)).data,
  listarRetificacoes: async (id: string): Promise<PreProjetoRetificacaoDTO[]> =>
    (await api.get(`/pre-projetos/${id}/retificacoes`)).data,
  select: async (id: string): Promise<PreProjetoDTO> =>
    (await api.post(`/pre-projetos/${id}/selecionar`)).data,
  /** Cancela a seleção: apaga a emenda gerada e devolve o pré-projeto a PUBLICADO (dentro do período configurado). */
  cancelarSelecao: async (id: string, justificativa: string): Promise<PreProjetoDTO> =>
    (await api.post(`/pre-projetos/${id}/cancelar-selecao`, { justificativa })).data,
};

export default preProjetoService;
